import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Edit,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  User,
  Mail,
  Phone,
  Calendar,
  Layers,
  Activity,
  UserCheck,
  Hash,
  Scale,
  ArrowUp,
  FileText,
  Clock
} from 'lucide-react';
import moment from 'moment';
import { studentsAPI, trainersAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../HasPermission';

// Defined outside the parent component so React doesn't remount these on every
// keystroke — defining them inline inside StudentDetailsTab caused inputs to
// lose focus after each character because each render produced a new Field
// component identity.
const InfoCard = ({ title, icon: Icon, children, colors, isDark }) => (
  <div className={`${colors.bgTertiary} border ${colors.border} rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300`}>
    <div className={`px-4 py-3 border-b ${colors.border} flex items-center gap-2.5 bg-gradient-to-r ${isDark ? 'from-white/[0.03] to-transparent' : 'from-gray-50 to-transparent'}`}>
      <div className={`p-1.5 rounded-lg ${isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-500/10 text-blue-600'}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <h3 className={`text-[10px] font-bold ${colors.textPrimary} uppercase tracking-widest`}>{title}</h3>
    </div>
    <div className="p-4 space-y-3.5">
      {children}
    </div>
  </div>
);

const Field = ({
  label,
  icon: Icon,
  value,
  field,
  type = "text",
  options,
  className = "",
  isEditing,
  formData,
  handleInputChange,
  colors,
  t,
}) => (
  <div className={`group ${className}`}>
    <div className="flex items-center gap-1.5 mb-1">
      {Icon && <Icon className={`w-3 h-3 ${colors.textTertiary} group-hover:text-blue-500 transition-colors shrink-0`} />}
      <label className={`text-[10px] font-bold ${colors.textTertiary} uppercase tracking-tighter truncate`}>{label}</label>
    </div>
    {isEditing ? (
      type === 'select' ? (
        <select
          value={formData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
          className={`w-full px-2 py-1.5 text-xs border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} rounded-lg focus:outline-none focus:ring-1 ${colors.inputFocus} appearance-none bg-no-repeat bg-right transition-all`}
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          value={formData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
          className={`w-full px-2 py-1.5 text-xs border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} rounded-lg focus:outline-none focus:ring-1 ${colors.inputFocus} transition-all`}
          {...(type === 'number' && { step: field === 'weight' || field === 'height' ? '0.1' : '1' })}
        />
      )
    ) : (
      <div className={`text-xs font-bold ${colors.textPrimary} truncate flex items-center min-h-[1.25rem]`}>
        {value || <span className="text-slate-500 italic font-normal text-[10px]">{t('studentDetails.values.unspecified')}</span>}
      </div>
    )}
  </div>
);

const StudentDetailsTab = ({ student, organizationId, onStudentUpdate }) => {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const canEditStudent = hasPermission('update:students');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [mainTrainerName, setMainTrainerName] = useState(null);

  // Resolve mainTrainerId -> trainer name. Backend returns the raw ID, so we
  // look it up here once per student. Falls back to the ID if the lookup
  // fails (deleted trainer, cross-org, etc.).
  useEffect(() => {
    let cancelled = false;
    const trainerId = student.mainTrainerId;
    const orgId = student.organizationId || organizationId;
    if (!trainerId || !orgId) {
      setMainTrainerName(null);
      return undefined;
    }
    (async () => {
      try {
        const res = await trainersAPI.getById(orgId, trainerId);
        const t = res?.data?.data || res?.data;
        if (cancelled || !t) return;
        const full = [t.name, t.surname].filter(Boolean).join(' ').trim();
        setMainTrainerName(full || null);
      } catch (e) {
        if (!cancelled) setMainTrainerName(null);
      }
    })();
    return () => { cancelled = true; };
  }, [student.mainTrainerId, student.organizationId, organizationId]);
  const [formData, setFormData] = useState({
    name: student.name || '',
    surname: student.surname || '',
    email: student.email || '',
    phone: student.phone || '',
    gender: student.gender || '',
    birthDate: student.birthDate ? moment(student.birthDate).format('YYYY-MM-DD') : '',
    age: student.age || '',
    bloodType: student.bloodType || '',
    tcNo: student.tcNo || '',
    weight: student.weight || '',
    height: student.height || '',
    notes: student.notes || '',
    studentStatus: student.studentStatus || 'active',
    startDate: student.startDate ? moment(student.startDate).format('YYYY-MM-DD') : ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const updateData = {
        ...formData,
        birthDate: formData.birthDate ? new Date(formData.birthDate).toISOString() : null,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        age: formData.age ? parseInt(formData.age) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        height: formData.height ? parseFloat(formData.height) : null
      };

      const response = await studentsAPI.update(organizationId, student.id, updateData);

      if (onStudentUpdate) {
        onStudentUpdate(response.data.data);
      }

      setIsEditing(false);
      setSuccessMessage(t('studentDetails.success'));

      setTimeout(() => setSuccessMessage(null), 3000);

    } catch (err) {
      console.error('Error updating student:', err);
      setError(t('studentDetails.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: student.name || '',
      surname: student.surname || '',
      email: student.email || '',
      phone: student.phone || '',
      gender: student.gender || '',
      birthDate: student.birthDate ? moment(student.birthDate).format('YYYY-MM-DD') : '',
      age: student.age || '',
      bloodType: student.bloodType || '',
      tcNo: student.tcNo || '',
      weight: student.weight || '',
      height: student.height || '',
      notes: student.notes || '',
      studentStatus: student.studentStatus || 'active',
      startDate: student.startDate ? moment(student.startDate).format('YYYY-MM-DD') : ''
    });
    setIsEditing(false);
    setError(null);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return t('studentDetails.values.active');
      case 'inactive': return t('studentDetails.values.inactive');
      case 'suspended': return t('studentDetails.values.suspended');
      case 'graduated': return t('studentDetails.values.graduated');
      default: return status;
    }
  };

  const getGenderLabel = (gender) => {
    switch (gender) {
      case 'Male': return t('studentDetails.values.male');
      case 'Female': return t('studentDetails.values.female');
      default: return t('studentDetails.values.unspecified');
    }
  };

  // Bundled props for the hoisted Field/InfoCard so call sites stay terse.
  const fieldCtx = { isEditing, formData, handleInputChange, colors, t };
  const cardCtx = { colors, isDark };

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Header Info */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-1">
        <div>
          <h2 className={`text-xl font-bold ${colors.textPrimary} tracking-tight`}>{t('studentDetails.title')}</h2>
          <p className={`text-[11px] ${colors.textSecondary}`}>{t('common.manageDetails', 'Manage student information and records')}</p>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                className={`flex items-center gap-1.5 px-4 py-1.5 border ${colors.btnSecondaryBorder} ${colors.btnSecondaryText} ${colors.btnSecondaryBg} rounded-lg ${colors.btnSecondaryHover} transition-all duration-200 font-bold text-xs`}
                disabled={saving}
              >
                <X className="w-3.5 h-3.5" />
                <span>{t('studentDetails.cancel')}</span>
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-1.5 px-5 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold text-xs rounded-lg hover:from-blue-500 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 shadow-lg shadow-blue-500/10"
                disabled={saving}
              >
                <Save className="w-3.5 h-3.5" />
                <span>{saving ? t('studentDetails.saving') : t('studentDetails.save')}</span>
              </button>
            </>
          ) : canEditStudent ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1.5 px-5 py-1.5 bg-white/[0.05] border border-white/10 text-white font-bold text-xs rounded-lg hover:bg-white/[0.1] hover:border-blue-500/40 transition-all duration-200 group shadow-sm bg-gradient-to-b from-white/[0.05] to-transparent"
            >
              <Edit className="w-3.5 h-3.5 group-hover:text-blue-500" />
              <span>{t('studentDetails.edit')}</span>
            </button>
          ) : null}
        </div>
      </div>

      {/* Messages */}
      {(error || successMessage) && (
        <div className={`flex items-center p-4 rounded-xl border ${error ? 'bg-red-500/10 border-red-500/50 text-red-500' : 'bg-green-500/10 border-green-500/50 text-green-500'}`}>
          {error ? <AlertCircle className="w-5 h-5 mr-3 shrink-0" /> : <CheckCircle className="w-5 h-5 mr-3 shrink-0" />}
          <span className="text-sm font-medium">{error || successMessage}</span>
          <button onClick={() => { setError(null); setSuccessMessage(null); }} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Personal Details Card */}
        <InfoCard title={t('studentDetails.personalInfo')} icon={User} {...cardCtx}>
          <div className="grid grid-cols-2 gap-x-3 gap-y-3.5">
            <Field label={t('studentDetails.fields.name')} field="name" value={student.name} {...fieldCtx} />
            <Field label={t('studentDetails.fields.surname')} field="surname" value={student.surname} {...fieldCtx} />

            <Field label={t('studentDetails.fields.gender')} icon={UserCheck} field="gender" type="select"
              value={getGenderLabel(student.gender)}
              options={[
                { value: '', label: t('studentDetails.placeholders.selectGender') },
                { value: 'Male', label: t('studentDetails.values.male') },
                { value: 'Female', label: t('studentDetails.values.female') }
              ]}
              {...fieldCtx}
            />
            <Field label={t('studentDetails.fields.age')} icon={Clock} field="age" type="number" value={student.age ? `${student.age} ${t('common.years', 'yaş')}` : null} {...fieldCtx} />
          </div>

          <div className="grid grid-cols-1 gap-y-3.5 pt-1">
            <Field label={t('studentDetails.fields.email')} icon={Mail} field="email" type="email" value={student.email} {...fieldCtx} />
            <Field label={t('studentDetails.fields.phone')} icon={Phone} field="phone" type="tel" value={student.phone} {...fieldCtx} />
            <Field label={t('studentDetails.fields.birthDate')} icon={Calendar} field="birthDate" type="date" value={student.birthDate ? moment(student.birthDate).format('DD/MM/YYYY') : null} {...fieldCtx} />
          </div>
        </InfoCard>

        {/* Health & Identity Card */}
        <InfoCard title={t('studentDetails.healthInfo')} icon={Activity} {...cardCtx}>
          <div className="grid grid-cols-2 gap-x-3 gap-y-3.5">
            <Field label={t('studentDetails.fields.bloodType')} icon={Layers} field="bloodType" type="select"
              value={student.bloodType}
              options={[
                { value: '', label: t('studentDetails.placeholders.selectBloodType') },
                ...['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(t => ({ value: t, label: t }))
              ]}
              {...fieldCtx}
            />
            <Field label={t('studentDetails.fields.tcNo')} icon={Hash} field="tcNo" value={student.tcNo} {...fieldCtx} />

            <Field label={t('studentDetails.fields.weight')} icon={Scale} field="weight" type="number" value={student.weight ? `${student.weight} kg` : null} {...fieldCtx} />
            <Field label={t('studentDetails.fields.height')} icon={ArrowUp} field="height" type="number" value={student.height ? `${student.height} cm` : null} {...fieldCtx} />
          </div>

          <div className="pt-2 border-t border-white/5 mt-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5 text-blue-500" />
                <span className={`text-[10px] font-bold ${colors.textTertiary} uppercase tracking-tight`}>{t('studentDetails.fields.bmi')}</span>
              </div>
              <span className={`text-base font-bold ${student.weight && student.height ? 'text-blue-500' : colors.textMuted}`}>
                {student.weight && student.height
                  ? (student.weight / ((student.height / 100) ** 2)).toFixed(1)
                  : '--'
                }
              </span>
            </div>
          </div>
        </InfoCard>

        {/* System & Records Card */}
        <InfoCard title={t('studentDetails.systemInfo')} icon={Layers} {...cardCtx}>
          <div className="grid grid-cols-1 gap-y-3.5">
            <div className="flex items-center justify-between">
               <Field
                label={t('studentDetails.fields.status')}
                icon={UserCheck}
                field="studentStatus"
                type="select"
                value={
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                    student.studentStatus === 'active' ? 'bg-green-500/20 text-green-500 border border-green-500/30' : 'bg-red-500/20 text-red-500 border border-red-500/30'
                  }`}>
                    {getStatusLabel(student.studentStatus)}
                  </span>
                }
                options={[
                  { value: 'active', label: t('studentDetails.values.active') },
                  { value: 'inactive', label: t('studentDetails.values.inactive') },
                  { value: 'suspended', label: t('studentDetails.values.suspended') },
                  { value: 'graduated', label: t('studentDetails.values.graduated') }
                ]}
                {...fieldCtx}
              />
            </div>

            <Field label={t('studentDetails.fields.startDate')} icon={Calendar} field="startDate" type="date" value={student.startDate ? moment(student.startDate).format('DD/MM/YYYY') : null} {...fieldCtx} />
            
            <div className="pt-1.5 space-y-3">
              <div className="group">
                <label className={`text-[10px] font-bold ${colors.textTertiary} uppercase tracking-tighter mb-0.5 block`}>{t('studentDetails.fields.mainTrainer')}</label>
                <div className={`text-xs font-bold ${colors.textPrimary} truncate`}>
                  {mainTrainerName
                    ? mainTrainerName
                    : <span className="text-slate-500 italic font-normal text-[10px]">{t('studentDetails.values.unspecified')}</span>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-3">
                <div className="group">
                  <label className={`text-[10px] font-bold ${colors.textTertiary} uppercase tracking-tighter mb-0.5 block`}>{t('studentDetails.fields.created')}</label>
                  <div className={`text-xs font-medium ${colors.textSecondary}`}>{student.createdAt ? moment(student.createdAt).format('DD/MM/YYYY') : '--'}</div>
                </div>
                <div className="group">
                  <label className={`text-[10px] font-bold ${colors.textTertiary} uppercase tracking-tighter mb-0.5 block`}>{t('studentDetails.fields.updated')}</label>
                  <div className={`text-xs font-medium ${colors.textSecondary}`}>{student.updatedAt ? moment(student.updatedAt).format('DD/MM/YYYY') : '--'}</div>
                </div>
              </div>
            </div>
          </div>
        </InfoCard>
      </div>

      {/* Notes Section with Premium Style */}
      <div className={`${colors.bgTertiary} border ${colors.border} rounded-2xl p-6 shadow-sm overflow-hidden relative group`}>
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 blur-3xl -mr-16 -mt-16 rounded-full group-hover:bg-blue-500/10 transition-all duration-700" />
        
        <div className="flex items-center gap-2 mb-4 relative z-10">
          <FileText className="w-4 h-4 text-blue-500" />
          <h3 className={`text-xs font-bold ${colors.textPrimary} uppercase tracking-wider`}>{t('studentDetails.notes')}</h3>
        </div>
        
        <div className="relative z-10">
          {isEditing ? (
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              className={`w-full px-4 py-3 text-sm border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} ${colors.inputPlaceholder} rounded-xl focus:outline-none focus:ring-2 ${colors.inputFocus} resize-none min-h-[120px] transition-all`}
              placeholder={t('studentDetails.placeholders.notes')}
            />
          ) : (
            <div className={`p-4 rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-gray-50'} border border-white/5`}>
              <p className={`text-sm ${colors.textSecondary} leading-relaxed whitespace-pre-wrap`}>
                {student.notes || t('studentDetails.placeholders.noNotes')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDetailsTab;